// src/store/thunks/withLoaderAndErrorHandling.ts
import type { AsyncThunkPayloadCreator } from '@reduxjs/toolkit';

import type { AppThunkConfig } from '@/types/Store';
import { setHideLoader, setShowLoader } from '@/store/slices/ui/uiSlice';
import { PollingError } from '@/services/polling/PollingError';
import { showErrorToast } from '@/utils/toast.utils';

import { isAPIError } from '../utils';

export function withLoader<Returned, ThunkArg>(
  payloadCreator: AsyncThunkPayloadCreator<Returned, ThunkArg, AppThunkConfig>
): AsyncThunkPayloadCreator<Returned, ThunkArg, AppThunkConfig> {
  // Disable the rule for this function signature
  // eslint-disable-next-line @typescript-eslint/promise-function-async -- we want to return a Promise but not mark the function as async
  return (arg, thunkAPI) => {
    const { dispatch } = thunkAPI;
    dispatch(setShowLoader());

    const promise = Promise.resolve(payloadCreator(arg, thunkAPI));

    return promise.finally(() => {
      dispatch(setHideLoader());
    });
  };
}

/**
 * Wraps a thunk with automatic API error handling
 */
export function withErrorHandling<Returned, ThunkArg>(
  payloadCreator: AsyncThunkPayloadCreator<Returned, ThunkArg, AppThunkConfig>
): AsyncThunkPayloadCreator<Returned, ThunkArg, AppThunkConfig> {
  // eslint-disable-next-line @typescript-eslint/promise-function-async -- we want to return a Promise but not mark the function as async
  return (arg, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;

    return Promise.resolve(payloadCreator(arg, thunkAPI)).catch((err: unknown) => {
      if (err instanceof PollingError) {
        if (typeof err.payload === 'object' && err.payload !== null && 'data' in err.payload) {
          const { payload } = err;
          const { data } = payload;
          let errorMsg: string | null = null;
          if (
            typeof data === 'object' &&
            data !== null &&
            'message' in data &&
            typeof data.message === 'string'
          ) {
            const { message } = data;
            errorMsg = message;
          } else if (typeof err.message === 'string') {
            const { message } = err as { message: string };
            errorMsg = message;
          }
          showErrorToast(errorMsg ?? 'An unexpected error occurred');
          return rejectWithValue({ message: err.message });
        }
      }
      if (isAPIError(err)) {
        // fieldErrors is absent (undefined) on client-thrown APIErrors and null
        // when the backend sends none — only a populated object means "let the
        // form render field errors instead of toasting".
        if (err.fieldErrors != null) {
          return rejectWithValue({ fieldErrors: err.fieldErrors });
        }
        showErrorToast(err.message);
        return rejectWithValue({ message: err.message });
      }
      throw err;
    });
  };
}
